/* eslint-disable security/detect-object-injection */
import {
  useCallback,
  useEffect,
  useMemo,
  useState,
  useRef,
  ReactNode,
} from 'react';
import {t} from 'i18next';
import clsx from 'clsx';
import {Box, Typography} from '@mui/material';
import {InfoOutlined} from '@mui/icons-material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import './DocList.scss';
import {
  GridColDef,
  GridFilterModel,
  GridRowId,
  GridSortModel,
} from '@mui/x-data-grid';
import {ReactComponent as NoDataIcon} from '../../assets/images/no-data-found.svg';
import {
  useDocListGrid,
  useDocumentListView,
  useGetDocListLoadmetaDataFilters,
  useGetDocListMetadataFilters,
  useGetDocumentListIdforAction,
  useSaveToProfile,
} from '../../hooks/document/useDocument';
import ArrTooltipWrapper from '../common/arr-tooltip-wrapper/ArrTooltipWrapper';
import ArrButton from '../common/arr-button/ArrButton';
import DocListFilterAccordion from './doc-list-filter/doc-list-filter-accordion/DocListFilterAccordion';
import ArrDataGridPro from '../common/arr-data-grid-pro/ArrDataGridPro';
import ArrPagination from '../common/arr-pagination/ArrPagination';
import ArrModal from '../common/arr-modal/ArrModal';
import SummaryViewerPopup from './summary-viewer-popup/SummaryViewerPopup';
import ArrRadio from '../common/arr-radio/ArrRadio';
import ArrCheckbox from '../common/arr-checkbox/ArrCheckbox';
import ActionHeader from './action-header/ActionHeader';
import FilterTextField from '../common/arr-data-grid-pro/filter-components/text-field/FilterTextField';
import {decodeHtmlEntities} from '../../utils/utils';
import FilterDateField from '../common/arr-data-grid-pro/filter-components/date-time-field/FilterDateField';
import {useDispatch, useSelector} from 'react-redux';
import {RootState} from '../../redux/store';
import ImageCardWithDetails from '../../pages/doc-explorer/doc-list-grid-view/ImageCardWithDetails';
import ArrLoadingSpinner from '../common/arr-loading-spinner/ArrLoadingSpinner';
import {
  GRID_DETAIL_PANEL_TOGGLE_FIELD,
  GridRenderHeaderFilterProps,
} from '@mui/x-data-grid-pro';
import {useNavigate} from 'react-router-dom';
import {
  IDocListGridDataResponse,
  IGetExportMasterDocListIdRequest,
} from '../../api/types/document.type';
import {ArrDrawer} from '../common/arr-drawer/ArrDrawer';
import ManageColumns from './manage-columns/ManageColumns';
import {setShowManageColumns} from '../../redux/slice/docListSlice';
import DocPreviewModal from '../document-view-prizmdoc/DocPreviewModal';
import CellWrapper from '../common/arr-data-grid-pro/filter-components/cell-wrapper/CellWrapper';
import {convertToUTCDate} from '../../utils/formatters';
import {
  RoleId,
  SourceTypeCode,
  SourceTypeContextCode,
  ToastType,
} from '../../shared/constants';
import {DocActionMenuItem} from '../../shared/interfaces';
import {useDocListActionForDocView} from './useDocListActionForDocView';
import ArrToastNotification from '../common/arr-toast-notification/ArrToastNotification';
import {CustomMenuItem} from '../common/arr-menu/ArrMenu';
import ActionMenuWrapper from '../action-menu/action-menu-wrapper/ActionMenuWrapper';
import {ReactComponent as ArrowDropDownIcon} from '../../assets/images/dropdown.svg';
import {ReactComponent as ArrowDropUpIcon} from '../../assets/images/dropup.svg';
import {useGetReportDocumentMasterDocId} from '../../hooks/reporting/useReporting';
import {IGetReportMasterDocListIdRequest} from '../../api/types/reportingApi.type';
import {setActiveDocument} from '../document-viewer/docViewerUtils';
import {storeDocumentListMasterId} from '../../redux/slice/docViewSlice';
import useKeyPress from '../../utils/hooks/keyboardKeyPress';
import {extractReportId} from '../background-tasks/backgroundTasksUtils';
import ArrDocMaxLimitWarning from '../common/arr-doc-max-limit-warning/ArrDocMaxLimitWarning';
import {clearCategorizationState} from '../../redux/slice/categorizationSlice';
import dayjs from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';
dayjs.extend(customParseFormat);

const NoRowsOverlay = () => (
  <Box className="no-document-data-found">
    <NoDataIcon />
    <Typography
      className="typo-text headline3_size no-document-text"
      data-testid={`doc-list-message`}
    >
      {t('docList.noData.message')}
    </Typography>
  </Box>
);

export const DOC_LIST_ACTION_MENU: DocActionMenuItem[] = [
  {
    id: 'View',
    name: 'View',
    Roles: [RoleId.ProjectAdministrator, RoleId.ReviewManager, RoleId.Reviewer],
    subItems: [
      {
        id: 'ViewInDocView',
        name: 'ViewInDocView',
        Roles: [
          RoleId.ProjectAdministrator,
          RoleId.ReviewManager,
          RoleId.Reviewer,
        ],
      },
      {
        id: 'ViewInDocViewInNewTab',
        name: 'ViewInDocViewInNewTab',
        Roles: [
          RoleId.ProjectAdministrator,
          RoleId.ReviewManager,
          RoleId.Reviewer,
        ],
      },
    ],
  },
  {
    id: 'BulkTag',
    name: 'BulkTag',
    Roles: [RoleId.ProjectAdministrator, RoleId.ReviewManager, RoleId.Reviewer],
  },
  {
    id: 'BulkFolder',
    name: 'BulkFolder',
    Roles: [RoleId.ProjectAdministrator, RoleId.ReviewManager, RoleId.Reviewer],
  },
  {
    id: 'BulkAssign',
    name: 'BulkAssign',
    Roles: [RoleId.ReviewManager],
  },
  {
    id: 'QuickBatch',
    name: 'QuickBatch',
    Roles: [RoleId.ReviewManager],
  },
  {
    id: 'BulkRelease',
    name: 'BulkRelease',
    Roles: [RoleId.ProjectAdministrator],
  },
  {
    id: 'Tally',
    name: 'Tally',
    Roles: [RoleId.ProjectAdministrator, RoleId.ReviewManager],
  },
  {
    id: 'ExportData',
    name: 'ExportData',
    Roles: [RoleId.ProjectAdministrator, RoleId.ReviewManager],
  },
];

export const fieldToApiColumnMap: Record<string, string> = {
  DocID: 'M--60',
  CustodianName: 'M--57',
  DocFileName: 'M--62',
  DocFileType: 'M--64',
  MasterDate: 'M--82',
};

export interface IDynamicColDef {
  FieldIDApi: string;
  FieldID: number | string;
  FieldName: string;
  FieldLabel: string;
  FieldType: string;
  SequenceNumber: number;
  FieldDataTye: string;
}

const hasTime = (dateString: string): boolean => {
  return /:/.test(dateString);
};
const DOC_LIST_DATE_INPUT_FORMATS = [
  'YYYY-MM-DDTHH:mm:ss.SSS[Z]',
  'YYYY-MM-DDTHH:mm:ss.SSS',
  'YYYY-MM-DDTHH:mm:ss[Z]',
  'YYYY-MM-DDTHH:mm:ssZ',
  'YYYY-MM-DDTHH:mm:ss',
  'YYYY-MM-DD HH:mm:ss.SSS',
  'YYYY-MM-DD HH:mm:ss',
  'YYYY-MM-DD HH:mm',
  'YYYY-MM-DDTHH:mm',
  'YYYY-MM-DD',
  'YYYY/MM/DD HH:mm:ss',
  'YYYY/MM/DD',
  'DD-MM-YYYYTHH:mm:ss',
  'DD-MM-YYYY HH:mm:ss',
  'DD-MM-YYYY',
];

const parseDocListDateString = (raw: string): dayjs.Dayjs | null => {
  const value = raw?.trim() ?? '';
  if (!value) {
    return null;
  }
  const parsed = dayjs(value, DOC_LIST_DATE_INPUT_FORMATS, true);
  return parsed.isValid() ? parsed : null;
};

const getUTCDate = (dateString: string) => {
  if (hasTime(dateString))
    return dayjs(dateString).format('YYYY/MM/DD HH:mm:ss');
  else return dayjs(dateString).format('YYYY/MM/DD');
};
const SelectedColumns: IDynamicColDef[] = [
  {
    FieldID: -60,
    FieldIDApi: 'M--60',
    FieldName: 'DocID',
    FieldLabel: 'DocID',
    FieldType: 'FIELD',
    SequenceNumber: 1,
    FieldDataTye: 'NVARCHAR',
  },
  {
    FieldID: -57,
    FieldIDApi: 'M--57',
    FieldName: 'CustodianName',
    FieldLabel: 'CustodianName',
    FieldType: 'FIELD',
    SequenceNumber: 2,
    FieldDataTye: 'NVARCHAR',
  },
  {
    FieldID: -62,
    FieldIDApi: 'M--62',
    FieldName: 'DocFileName',
    FieldLabel: 'DocFileName',
    FieldType: 'FIELD',
    SequenceNumber: 3,
    FieldDataTye: 'NVARCHAR',
  },
  {
    FieldID: -64,
    FieldIDApi: 'M--64',
    FieldName: 'DocFileType',
    FieldLabel: 'DocFileType',
    FieldType: 'FIELD',
    SequenceNumber: 4,
    FieldDataTye: 'NVARCHAR',
  },
  {
    FieldID: -82,
    FieldIDApi: 'M--82',
    FieldName: 'MasterDate',
    FieldLabel: 'MasterDate',
    FieldType: 'FIELD',
    SequenceNumber: 5,
    FieldDataTye: 'DATETIME',
  },
];

const defaultDataColumns = [
  {
    mData: 'ExpandIcon',
    sName: 'ExpandIcon',
    sTitle: '',
    visible: true,
    sDefaultContent: '',
    bSortable: false,
  },
  {
    mData: 'IsSelected',
    sName: 'IsSelected',
    sTitle:
      '<label class="checkbox" style="line-height: 1;"><input type="checkbox" onchange="BindCheckedData(this)" name="checkbox" id="selectAllRows" /><i></i></label>',
    visible: true,
    bSortable: false,
  },
  {
    mData: 'Action',
    sName: 'Action',
    sTitle: '',
    visible: true,
    sDefaultContent: '',
    bSortable: false,
  },
  {
    mData: 'image',
    sName: 'image',
    sTitle: 'image',
    visible: true,
    sDefaultContent: '',
    bSortable: false,
  },
];

export type ComboPanelMetadata = {
  docId: string;
  custodianName: string;
  docFileName: string;
  docFileType: string;
  masterDate: string;
  imgUrl: string;
};

export interface DocForPreview {
  id: number;
  docId: string;
  docName: string;
}

type GlobalSelectionMode = 'none' | 'select' | 'unselect';

type SelectionStatePayload = {
  IsInverse: boolean;
  IsAllChildrenSelected: boolean;
  SelectedIds: Array<string | number>;
  DocsToExclude: Array<string | number>;
};

// ------------ Component --------------
const DocList = () => {
  const navigate = useNavigate();

  const [selectionState, setSelectionState] = useState<SelectionStatePayload>({
    IsInverse: false,
    IsAllChildrenSelected: false,
    SelectedIds: [],
    DocsToExclude: [],
  });

  const [appliedSelectionOptions, setAppliedSelectionOptions] = useState({
    selectAllIntent: 'select' as 'select' | 'unselect',
    selectAllPages: 'no' as 'yes' | 'no',
    selectAllChildren: 'no' as 'yes' | 'no',
  });

  const [globalSelection, setGlobalSelection] = useState<{
    mode: GlobalSelectionMode;
    includeChildren: boolean;
  }>({mode: 'none', includeChildren: false});

  const [summaryModal, setSummaryModal] = useState<{
    enable: boolean;
    docId: string;
    summary: string;
  }>({enable: false, docId: '', summary: ''});
  const [summaryAnchorEl, setSummaryAnchorEl] = useState<HTMLElement | null>(
    null
  );

  const [childScope, setChildScope] = useState<{
    scope: 'currentPage' | 'allPages';
    mode: 'select' | 'unselect';
  }>({scope: 'currentPage', mode: 'select'});

  const [selectedRows, setSelectedRows] = useState<Set<string | number>>(
    new Set()
  );

  const [filterModel, setFilterModel] = useState<GridFilterModel>({
    items: [],
  });

  const [filterSecondaryModel, setFilterSecondaryModel] =
    useState<GridFilterModel>({
      items: [],
    });

  const [rows, setRows] = useState<any>([]);

  const [selectedColumns, setSelectedColumns] = useState<IDynamicColDef[]>([]);

  const {data: metadataFilters} = useGetDocListMetadataFilters();
  const memoizedMetadataFilters = useMemo(
    () => metadataFilters ?? [],
    [metadataFilters]
  );

  const [showAllSelectionModal, setShowAllSelectionModal] = useState(false);

  const [selectAllIntent, setSelectAllIntent] = useState<'select' | 'unselect'>(
    'select'
  );

  const [selectAllPages, setSelectAllPages] = useState<'yes' | 'no'>('no');

  const [selectAllChildren, setSelectAllChildren] = useState<string>('no');

  const [showDocLimitWarning, setShowDocLimitWarning] =
    useState<boolean>(false);
  const selectedMenuItem = useRef<CustomMenuItem | undefined>(undefined);

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [selectAllApplied, setSelectAllApplied] = useState(false);

  const [showParentSelectionModal, setShowParentSelectionModal] =
    useState(false);

  const [currentParentRowId, setCurrentParentRowId] = useState<
    string | number | null
  >(null);

  const [isParentChecked, setIsParentChecked] = useState<boolean>(false);

  const [gridData, setGridData] = useState<IDocListGridDataResponse | null>(
    null
  );

  const [sortModel, setSortModel] = useState<GridSortModel>([
    {
      field: 'DocID',
      sort: 'asc',
    },
  ]);

  const [itemsPerPage, setItemsPerPage] = useState<number>(10);
  const [pageNumber, setPageNumber] = useState(1);

  const [searchText] = useState<string>('');

  const [showGridView, setShowGridView] = useState(false);

  const [expandedRowIds, setExpandedRowIds] = useState<Set<GridRowId>>(
    new Set()
  );

  const [globalUnselectedIds, setGlobalUnselectedIds] = useState<
    Set<string | number>
  >(new Set());

  const [showSaveModal, setShowSaveModal] = useState(false);

  const [showToast, setShowToast] = useState(false);
  const [toastMessage, setToastMessage] = useState('');
  const [toastType, setToastType] = useState(ToastType.Success);
  const [selectedRangeParentList, setSelectedRangeParentList] = useState([]);

  const ROW_HEIGHT = 36;
  const HEADER_HEIGHT = 48;
  const FILTER_HEIGHT = 56;
  const DEFAULT_HEIGHT_ADJUSTMENT = 8;

  const lastApiCallRef = useRef<string>('');

  const {docListId, backSourceLabel, backSourcePath, backSourceNavState} =
    useSelector((state: RootState) => state.docList);
  const docViewDocumentListMasterId = useSelector(
    (state: RootState) => state.docView?.documentListMasterId ?? ''
  );

  const docList = useSelector((state: RootState) => state.docList);

  const {
    mutate: saveToProfileMutate,
    isPending: isSaveToProfilePending,
    reset: resetSaveToProfile,
  } = useSaveToProfile();

  const {mutate: documentListViewMutate} = useDocumentListView();
  const isShiftPressed = useKeyPress('Shift');
  const lastSelectedIndexRef = useRef<number | null>(null);

  useEffect(() => {
    const payload = {
      docListViewId: 1,
      tallyFilter: {
        sourceType: '',
        sourceID: [],
        tally: {
          tallyByID: '',
          tallyBy: '',
          tallyByType: '',
          tallyByIDExceptMetadata: '',
          subTallyByID: '',
          subTallyByIDExceptMetadata: '',
          subTallyByType: '',
          tallyResultIDExceptMetadata: '',
          tallyResultID: '',
          subTallyByResultIDExceptMetadata: '',
        },
        subTally: {
          tallyByID: '',
          tallyBy: '',
          tallyByType: '',
          tallyByIDExceptMetadata: '',
          subTallyByID: '',
          subTallyByIDExceptMetadata: '',
          subTallyByType: '',
          tallyResultIDExceptMetadata: '',
          tallyResultID: '',
          subTallyByResultIDExceptMetadata: '',
        },
        navigationSourceType: '',
        reportFilePath: '',
        reportName: '',
      },
      previousUrl: '',
      pageSource: '',
      docViewSessionObjects: {
        sortconfiguration: {
          optimizedCols: '',
          optimizedSelectFieldIDs: '',
          optimizedSelectFieldOrder: '',
          manualCols: '',
          manualSelectFieldIDs: '',
          manualSelectFieldOrder: '',
          selectFields: '',
          configuredCols: '',
          order: '',
          selectFieldTemp: '',
          selectFieldDataTypeOptimized: [],
          selectFieldDataType: [],
          selectFieldDataTypeManual: [],
          manualOrder: '',
        },
        assignmentSortDetails: [],
        sourceTypeCode: '',
        assignmentSource: '',
        xmlCompletedDocumentEditedCodingForm: '',
        docsForCodeSame: [],
        backtoSrcSourceTypeCode: '',
      },
    };

    documentListViewMutate(payload, {
      onSuccess: (res) => {
        if (
          res.columnFilter.columnHeaders &&
          res.columnFilter.columnHeaders.length > 0
        ) {
          const columnsFromResponse = res.columnFilter.columnHeaders.map(
            (col) => ({
              FieldID: col.fieldID,
              FieldType: col.fieldType,
              FieldName: col.fieldName,
              FieldLabel: col.fieldLabel,
              SequenceNumber: col.sequenceNumber,
              FieldDataTye: col.fieldDataTye,
              FieldIDApi:
                col.fieldType === 'AIGENERATEDSUMMARY'
                  ? `AIGEN-${col.fieldID}`
                  : (res.docListHeaders.find(
                      (header) => header.sTitle === col.fieldName
                    )?.mData ?? ''),
            })
          );
          setSelectedColumns(columnsFromResponse);
        } else {
          setSelectedColumns(SelectedColumns);
        }
      },
      onError: () => {
        setSelectedColumns(SelectedColumns);
      },
    });

    return () => {
      const currentPath = `${
        window.location.pathname
      }${window.location.search}`.toLowerCase();
      const docListPath = 'Document/DocList';
      if (
        currentPath.includes(backSourcePath?.toLowerCase() ?? '') ||
        currentPath.includes(docListPath.toLowerCase())
      )
        return;
      dispatch(clearCategorizationState());
    };
  }, []);

  const selectedParentRow = useMemo(() => {
    return Array.from(selectedRows).find((rowId) => {
      // Parent rows have no '-' in their id
      return typeof rowId === 'string' && !rowId.includes('-');
    });
  }, [selectedRows]);

  const selectedDocListId =
    selectedParentRow ??
    docListId ??
    (backSourceLabel === t('docView.title') ? docViewDocumentListMasterId : null);

  const numericSelectedDocListId = useMemo(() => {
    if (typeof selectedDocListId === 'number') return selectedDocListId;
    if (typeof selectedDocListId === 'string') {
      const digits = selectedDocListId.replace(/\D+/g, '');
      const parsed = Number(digits);
      return Number.isFinite(parsed) && parsed > 0
        ? parsed
        : Number(docListId ?? 0);
    }
    return Number(docListId ?? 0);
  }, [selectedDocListId, docListId]);

  const parentFilter = metadataFilters?.find((filter: any) => filter.selected);
  const selectedFieldId = parentFilter?.projectFieldId ?? '-57';

  const queryKeyParams = useMemo(() => {
    const params = new URLSearchParams();
    params.append('DocListId', String(numericSelectedDocListId));
    params.append('FieldId', String(selectedFieldId));
    if (searchText) {
      params.append('Query', searchText);
    }
    params.append('PageNumber', String(pageNumber));
    params.append('PageSize', String(itemsPerPage));
    return `?${params.toString()}`;
  }, [
    selectedDocListId,
    selectedFieldId,
    itemsPerPage,
    pageNumber,
    searchText,
  ]);

  const {isDocViewActionLoading, onDocViewAction} =
    useDocListActionForDocView();

  const {data: loadMetadataFilters} =
    useGetDocListLoadmetaDataFilters(queryKeyParams);

  const showManageColumns = useSelector(
    (state: RootState) => state.docList.showManageColumns
  );

  const handleItemsPerPageChange = useCallback((newPerPage: number) => {
    setItemsPerPage(newPerPage);
  }, []);

  const handlePageNumberChange = useCallback((newPageNumber: number) => {
    setPageNumber(newPageNumber);
  }, []);

  const handleSortModelChange = useCallback((model: GridSortModel) => {
    setSortModel(model);
  }, []);

  const [selectedDocForPreview, setSelectedDocForPreview] =
    useState<DocForPreview | null>(null);

  const showSummary = ({
    docId,
    summary,
    anchorEl,
  }: {
    docId: string;
    summary: string;
    anchorEl: HTMLElement;
  }) => {
    setSummaryModal({enable: true, docId, summary});
    setSummaryAnchorEl(anchorEl);
  };

  useEffect(() => {
    if (showAllSelectionModal) {
      setSelectAllPages('no');
      setSelectAllChildren('no');
    }
  }, [showAllSelectionModal]);

  const sortingColumn = useMemo(
    () =>
      fieldToApiColumnMap[sortModel[0]?.field] ??
      sortModel[0]?.field ??
      'M--60',
    [sortModel]
  );

  const sortDirection = useMemo(
    () => sortModel[0]?.sort?.toUpperCase() || 'ASC',
    [sortModel]
  );

  const loginUserData = useSelector(
    (state: RootState) => state.authenticatedUser.user
  );

  const actionMenuItems: CustomMenuItem[] = (() => {
    const userRole = String(
      loginUserData.roleId ?? loginUserData.baseRoleId ?? ''
    );
    const mapAndFilter = (items: DocActionMenuItem[]): CustomMenuItem[] =>
      items
        .filter((it) => !it.Roles || it.Roles.includes(userRole))
        .map((it) => ({
          id: String(it.id),
          label: t(`docList.actionMenu.items.${it.name}`),
          disabled: it.disabled,
          submenu: it.subItems ? mapAndFilter(it.subItems) : undefined,
        }));
    return mapAndFilter(DOC_LIST_ACTION_MENU);
  })();

  const docListPayload = useMemo(
    () => ({
      docListId: numericSelectedDocListId,
      sortingColumn: sortingColumn,
      pageNumber: pageNumber,
      displayLength: itemsPerPage,
      direction: sortDirection,
      filters: [] as Array<{
        projectFieldId: string;
        projectFieldName: string;
        dataType: string;
        projectFieldLabel: string;
        type: string;
        operand: 'IN' | 'NOTIN' | 'BETWEEN' | 'BEFORE' | 'AFTER' | 'ON';
        values: string[];
      }>,
      columns: selectedColumns.map((col) => ({
        FieldID: col.FieldID,
        FieldType: col.FieldType,
        FieldLabel: col.FieldLabel,
        SequenceNumber: col.SequenceNumber,
        FieldDataTye: col.FieldDataTye,
        FilterValue: '',
        FieldIDApi: col.FieldIDApi,
      })),
      thumbView: false,
    }),
    [
      selectedColumns,
      numericSelectedDocListId,
      sortingColumn,
      pageNumber,
      itemsPerPage,
      sortDirection,
    ]
  );

  const dispatch = useDispatch();

  const updateColumnsWithFilterValues = (columns: IDynamicColDef[]) => {
    return columns.map((col) => {
      const matchingFilter = filterSecondaryModel.items.find(
        (item) =>
          item.field === col.FieldIDApi &&
          item.value !== undefined &&
          item.value !== null
      );
      return {
        FieldID: col.FieldID,
        FieldType: col.FieldType,
        FieldName: col.FieldLabel,
        FieldLabel: col.FieldLabel,
        SequenceNumber: col.SequenceNumber,
        FieldDataTye: col.FieldDataTye,
        FilterValue: matchingFilter ? matchingFilter.value.toString() : '',
      };
    });
  };

  const buildFiltersArray = useCallback((): Array<{
    ProjectFieldId: string;
    ProjectFieldName: string;
    dataType: string;
    ProjectFieldLabel: string;
    type: number;
    operand:
      | 'IN'
      | 'NOTIN'
      | 'BETWEEN'
      | 'BEFORE'
      | 'AFTER'
      | 'ON'
      | 'BELOW'
      | 'OVER'
      | 'AT';
    values: string[];
  }> => {
    if (!filterModel.items || filterModel.items.length === 0) {
      return [];
    }

    // Map string dataType to numeric "type"
    const mapTypeFromDataType = (dt?: string): number => {
      switch ((dt || '').toUpperCase()) {
        case 'NVARCHAR':
          return 2;
        case 'DATETIME':
          return 1;
        case 'INT':
          return 3;
        default:
          return 0;
      }
    };

    return filterModel.items
      .filter((item) => item.value && item.value.trim() !== '')
      .map((item: any) => {
        const metadata = memoizedMetadataFilters.find(
          (f: any) => f.projectFieldId === item.field
        );
        const dataType = metadata?.dataType || 'NVARCHAR';
        const numericType = mapTypeFromDataType(dataType);
        let values: string[] = [];
        if (dataType === 'DATETIME') {
          values = item.value.split('|').filter((v: string) => v.trim() !== '');
        } else if (dataType === 'INT') {
          values = item.value.split('|').filter((v: string) => v.trim() !== '');
        } else if (dataType === 'NVARCHAR') {
          values = item.value
            .split('|')
            .filter((v: string) => v.trim() !== '')
            .map((v: string) => {
              if (v.startsWith("'") && v.endsWith("'")) {
                return `'${v}'`;
              }
              return v;
            });
        } else {
          values = item.value.split('|').filter((v: string) => v.trim() !== '');
        }

        let operand:
          | 'IN'
          | 'NOTIN'
          | 'BETWEEN'
          | 'BEFORE'
          | 'AFTER'
          | 'ON'
          | 'BELOW'
          | 'OVER'
          | 'AT' = 'IN';

        if (dataType === 'DATETIME') {
          if (item.selectionType === 'before') {
            operand = 'BEFORE';
          } else if (item.selectionType === 'after') {
            operand = 'AFTER';
          } else if (item.selectionType === 'on') {
            operand = 'ON';
          } else if (item.selectionType === 'between') {
            operand = 'BETWEEN';
          } else {
            operand = 'IN'; // fallback
          }
        } else if (dataType === 'INT') {
          // For INT (file size), use specific operands based on selectionType
          if (item.selectionType === 'below') {
            operand = 'BELOW';
          } else if (item.selectionType === 'over') {
            operand = 'OVER';
          } else if (item.selectionType === 'at') {
            operand = 'AT';
          } else if (item.selectionType === 'between') {
            operand = 'BETWEEN';
          } else {
            operand = 'IN'; // fallback
          }
        } else {
          operand = item.operator === 'notEquals' ? 'NOTIN' : 'IN';
        }

        return {
          ProjectFieldId: metadata?.projectFieldId || '',
          ProjectFieldName: metadata?.projectFieldName || '',
          dataType: dataType,
          ProjectFieldLabel: metadata?.projectFieldLabel || '',
          type: numericType,
          operand,
          values,
        };
      });
  }, [filterModel, memoizedMetadataFilters]);

  const {
    mutate: getDocListGridData,
    data: docListGrid,
    isPending: docListGridLoading,
    isError: docListGridError,
  } = useDocListGrid(
    (data) => {
      setGridData(data);
    },
    (err) => {
      console.error('Failed to fetch doc list grid data:', err);
      setGridData(null);
    }
  );

  const docListGridResult = gridData || docListGrid;

  const docListGridData = useMemo(() => {
    const raw = docListGridResult?.data as unknown;
    if (!raw) return [];

    const tryParse = (val: unknown): any[] => {
      if (!val) return [] as any[];
      if (Array.isArray(val)) return val as any[];
      if (typeof val === 'string') {
        try {
          const parsed = JSON.parse(val);
          return Array.isArray(parsed) ? (parsed as any[]) : [];
        } catch {
          return [] as any[];
        }
      }
      if (typeof val === 'object') {
        const obj = val as Record<string, unknown>;
        if ('data' in obj) {
          const inner = obj['data'];
          const arr: any[] = tryParse(inner);
          if (arr.length) return arr;
        }
        for (const v of Object.values(obj)) {
          const arr: any[] = tryParse(v);
          if (arr.length) return arr;
        }
      }
      return [] as any[];
    };

    return tryParse(raw);
  }, [docListGridResult?.data]);

  useEffect(() => {
    if (!numericSelectedDocListId) {
      return;
    }

    const updatedColumns = updateColumnsWithFilterValues(
      docListPayload.columns
    );
    const filters = filterModel.items.length > 0 ? buildFiltersArray() : [];
    const docPayload = {
      ...docListPayload,
      thumbView: showGridView,
      columns: updatedColumns,
      filters: filters,
    };

    const payloadHash = JSON.stringify({
      columns: docPayload.columns,
      docListId: docPayload.docListId,
      sortingColumn: docPayload.sortingColumn,
      pageNumber: docPayload.pageNumber,
      displayLength: docPayload.displayLength,
      direction: docPayload.direction,
      thumbView: docPayload.thumbView,
      filters: docPayload.filters,
    });

    lastApiCallRef.current = payloadHash;

    if (selectedColumns && selectedColumns.length > 0) {
      getDocListGridData(docPayload);
    }
  }, [
    selectedColumns,
    filterModel,
    numericSelectedDocListId,
    sortingColumn,
    pageNumber,
    itemsPerPage,
    sortDirection,
    showGridView,
    filterSecondaryModel,
  ]);

  const prevChildScopeRef = useRef({...childScope, includeChildren: false});

  // Helpers for ID classification
  const isParentRowId = (id: string | number) =>
    typeof id === 'string' && id.startsWith('parent-');
  const isChildRowId = (id: string | number) =>
    typeof id === 'string' && id.startsWith('child-');

  const isRowCheckedEffective = (rowId: string | number) => {
    const isParent = isParentRowId(rowId);
    const isChild = isChildRowId(rowId);

    if (globalSelection.mode === 'select') {
      if (isParent) return !globalUnselectedIds.has(rowId);
      if (isChild) {
        if (globalSelection.includeChildren) {
          return !globalUnselectedIds.has(rowId);
        }
        return selectedRows.has(rowId);
      }
    }
    if (globalSelection.mode === 'unselect') {
      if (isParent) return false;
      if (isChild)
        return globalSelection.includeChildren
          ? false
          : selectedRows.has(rowId);
    } else {
      if (
        childScope.mode === 'unselect' &&
        childScope.scope === 'currentPage'
      ) {
        if (isParent) return !globalUnselectedIds.has(rowId);
      }
      if (isChild) {
        if (childScope.mode === 'unselect' && childScope.scope === 'allPages') {
          if (globalSelection.includeChildren) {
            return false;
          }
          return !prevChildScopeRef.current.includeChildren
            ? selectedRows.has(rowId)
            : !globalUnselectedIds.has(rowId);
        }
        if (
          childScope.mode === 'unselect' &&
          childScope.scope === 'currentPage'
        ) {
          if (globalSelection.includeChildren) {
            return selectedRows.has(rowId);
          }
          return !prevChildScopeRef.current.includeChildren
            ? selectedRows.has(rowId)
            : !globalUnselectedIds.has(rowId);
        }
      }
    }
    return selectedRows.has(rowId);
  };

  const getCurrentPageParentIds = () => {
    const ids: Array<string | number> = [];
    rows.forEach((row: any) => ids.push(row.id));
    return ids;
  };

  const getCurrentPageChildIds = () => {
    const ids: Array<string | number> = [];
    rows.forEach((row: any) => {
      const parentDocId = row.DocumentID;
      const childIds = getChildIdsForParent(parentDocId);
      ids.push(...childIds);
    });
    return ids;
  };
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const getCurrentPageAllIds = () => {
    return [...getCurrentPageParentIds(), ...getCurrentPageChildIds()];
  };

  const getParentDocIdFromRowId = (rowId: string | number) =>
    String(rowId).replace(/^parent-/, '');

  const getChildIdsForParent = (parentDocId: string | number) => {
    const parentRow = docListGridData.find(
      (r: any) => String(r.DocumentID) === String(parentDocId)
    );
    const children = Array.isArray(parentRow?.ChildDocuments?.Document)
      ? parentRow.ChildDocuments.Document
      : parentRow?.ChildDocuments?.Document
        ? [parentRow.ChildDocuments.Document]
        : [];
    return children
      .filter((c: any) => c?.DocumentID)
      .map((c: any) => `child-${parentDocId}-${c.DocumentID}`);
  };

  const onGridListToggle = (value: boolean) => {
    if (value) {
      getDocListGridData({...docListPayload, thumbView: true});
    } else {
      getDocListGridData({...docListPayload, thumbView: false});
    }
    setShowGridView(value);
  };

  const handleViewInDocView = useCallback(
    (actionId: string, totalDocumentToView: number) => {
      const sourceDetails = Array.from(selectionState.SelectedIds).map(
        (item: string | number) => {
          return {
            sourceID: item,
          };
        }
      ) as never;
      const docsToExclude = Array.from(selectionState.DocsToExclude).map(
        (item: string | number) => {
          return {
            sourceID: item,
          };
        }
      ) as never;

      const updatedColumns = updateColumnsWithFilterValues(
        docListPayload.columns as never
      );

      const objSourceOverrides = {
        totalDocumentToView,
      };

      const payload = {
        actionId,
        isAllPages: selectionState.IsInverse,
        isChildrens: selectionState.IsAllChildrenSelected,
        sourceDetails,
        docsToExclude,
        updatedColumns,
        documentListMasterID: numericSelectedDocListId,
        totalRecords: docListGridResult?.totalRecords,
        objSourceOverrides,
      };

      onDocViewAction(payload);
    },
    [
      docListGridResult?.totalRecords,
      docListPayload.columns,
      numericSelectedDocListId,
      onDocViewAction,
      selectionState.DocsToExclude,
      selectionState.IsAllChildrenSelected,
      selectionState.IsInverse,
      selectionState.SelectedIds,
      updateColumnsWithFilterValues,
    ]
  );

  const {
    mutate: reportMasterDocListId,
    isPending: isReportMasterDocListLoading,
  } = useGetReportDocumentMasterDocId(
    (response) => {
      //Derive source from API response
      const source = response?.reportMasterDoclistId
        ? [String(response.reportMasterDoclistId)]
        : [];

      navigate('/Report/Tally', {
        state: {
          navigationSourceType: 'DOCLIST',
          source,
          sourceName: 'DOCLIST',
        },
      });
    },
    () => {
      triggerToast(ToastType.Error, t('docList.getReportMasterDocListErr'));
    }
  );

  const {
    mutate: exportMasterDocListIdForAction,
    isPending: isDocumentListIdForActionLoading,
  } = useGetDocumentListIdforAction(
    (response) => {
      const source = response?.docListId ? String(response.docListId) : '';
      navigate('/Report/ExportData', {
        state: {
          item: {
            navigationSourceType: SourceTypeCode.DOCLIST,
            source,
            sourceName: SourceTypeCode.DOCUMENT,
            sourceDisable: true,
          },
        },
      });
    },
    () => {
      triggerToast(ToastType.Error, t('docList.getReportMasterDocListErr'));
    }
  );
  const onActionSelect = (action: CustomMenuItem | null) => {
    if (!action) return;
    const actionId = String(action.id || '').trim();

    if (actionId.toLowerCase() === 'tally') {
      const reportPayload: IGetReportMasterDocListIdRequest = {
        sourceTypeCode: 'DOCUMENT',
        isInverse: selectionState.IsInverse,
        documentListMasterID: numericSelectedDocListId,
        destinationContextTypeCode: 'TALLY',
        totalDocumentToView: -1,
        sourceDetails: [...selectionState.SelectedIds].map((i) => ({
          sourceID: Number(i),
        })),
        isAllChildrenSelected: selectionState.IsAllChildrenSelected,
        docsToExclude: [...selectionState.DocsToExclude].map((i) => ({
          sourceID: Number(i),
        })),
        columnFilters: updateColumnsWithFilterValues(
          docListPayload.columns as never
        ),
        Filters: buildFiltersArray(),
      };

      reportMasterDocListId(reportPayload);
      return;
    } else if (actionId.toLowerCase() === 'exportdata') {
      const exportPayload: IGetExportMasterDocListIdRequest = {
        sourceTypeCode: SourceTypeCode.DOCUMENT,
        isInverse: selectionState.IsInverse,
        documentListMasterID: numericSelectedDocListId,
        destinationTypeCode: SourceTypeCode.EXPORTDATA,
        sourceDetails: [...selectionState.SelectedIds].map((i) => ({
          sourceID: Number(i),
        })),
        isAllChildrenSelected: selectionState.IsAllChildrenSelected,
        docsToExclude: [...selectionState.DocsToExclude].map((i) => ({
          sourceID: Number(i),
        })),
        columnFilters: updateColumnsWithFilterValues(
          docListPayload.columns as never
        ),
        Filters: buildFiltersArray(),
      };

      exportMasterDocListIdForAction(exportPayload);
      return;
    }

    if (actionId === 'ViewInDocView' || actionId === 'ViewInDocViewInNewTab') {
      selectedMenuItem.current = action;
      setShowDocLimitWarning(true);
    }
  };

  const onContinueDocLimit = useCallback(
    (totalDocumentToView: number) => {
      setShowDocLimitWarning(false);
      const actionId = String(selectedMenuItem.current?.id || '').trim();
      handleViewInDocView(actionId, totalDocumentToView);
    },
    [handleViewInDocView]
  );

  const onCancelDocLimit = useCallback(() => {
    setShowDocLimitWarning(false);
  }, []);

  const globalParentUnselectedIds = useMemo(() => {
    return new Set(
      Array.from(globalUnselectedIds).filter((id) => isParentRowId(id))
    );
  }, [globalUnselectedIds]);

  const hasAnySelectionAnywhere = useMemo(
    () =>
      globalSelection.mode === 'select' ||
      selectedRows.size > 0 ||
      globalUnselectedIds.size > 0,
    [globalSelection.mode, selectedRows, globalUnselectedIds]
  );

  const renderAllSelectionCheckbox = () => {
    const parentIds = getCurrentPageParentIds();
    const selectedOnPage = parentIds.filter((id) =>
      isRowCheckedEffective(id)
    ).length;

    const isGlobalAllSelected =
      globalSelection.mode === 'select' && globalUnselectedIds.size === 0;

    const isPageIndeterminate =
      selectedOnPage > 0 && selectedOnPage < parentIds.length;

    const checked = isGlobalAllSelected;
    const isIndeterminate =
      !checked && (hasAnySelectionAnywhere || isPageIndeterminate);

    return (
      <ArrCheckbox
        checked={checked}
        isIndeterminate={isIndeterminate}
        size="medium"
        data-testid="select-all-documents"
        aria-label={`select-all-docs-cb`}
        onChange={() => {
          setSelectAllIntent(checked ? 'unselect' : 'select');
          setShowAllSelectionModal(true);
        }}
      />
    );
  };

  const handleRangeSelection = (row: any) => {
    const rowNumber = Number(row.RowNUMBER);
    const newSelectedRows = new Set(selectedRows);

    const start =
      Math.min(lastSelectedIndexRef.current ?? 0, rowNumber) === rowNumber
        ? rowNumber - 1
        : Math.min(lastSelectedIndexRef.current ?? 0, rowNumber);

    const end = Math.max(lastSelectedIndexRef.current ?? 0, rowNumber);
    const rangeSelection = rows.slice(start, end).map((row: any) => row.id);

    rangeSelection.forEach((item: string) => newSelectedRows.add(item));
    setSelectedRows(newSelectedRows);
    setGlobalUnselectedIds((prev) => {
      const next = new Set(prev);
      rangeSelection.forEach((item: string) => next.delete(item));
      return next;
    });
    const childAvailableRow = rangeSelection.filter((item: number) => {
      const parentDocId = getParentDocIdFromRowId(item);
      const childIds = getChildIdsForParent(parentDocId);
      const hasChildren = childIds.length > 0;
      const allChildrenSelectedEffective =
        hasChildren && childIds.every((id) => isRowCheckedEffective(id));
      if (hasChildren && !allChildrenSelectedEffective) {
        return true;
      }
    });
    if (childAvailableRow?.length) {
      setSelectedRangeParentList(childAvailableRow);
      setIsParentChecked(true);
      setShowParentSelectionModal(true);
    }
  };

  const renderSingleSelectionCheckbox = (params: any) => {
    const row = params.row;
    const rowNumber = Number(row.RowNUMBER);
    const isChecked = isRowCheckedEffective(row.id);

    const toggleRow = (checked: boolean) => {
      if (isChildRowId(row.id)) {
        const newSelectedRows = new Set(selectedRows);
        if (checked) {
          newSelectedRows.add(row.id);
          setSelectedRows(newSelectedRows);
          setGlobalUnselectedIds((prev) => {
            const next = new Set(prev);
            next.delete(row.id);
            return next;
          });
        } else {
          if (
            globalSelection.mode === 'select' &&
            globalSelection.includeChildren
          ) {
            setGlobalUnselectedIds((prev) => {
              const next = new Set(prev);
              next.add(row.id);
              return next;
            });
          } else {
            newSelectedRows.delete(row.id);
            setSelectedRows(newSelectedRows);
          }
        }
        return;
      }

      const parentDocId = getParentDocIdFromRowId(row.id);
      const childIds = getChildIdsForParent(parentDocId);
      const hasChildren = childIds.length > 0;
      const allChildrenSelectedEffective =
        hasChildren && childIds.every((id) => isRowCheckedEffective(id));
      const anyChildSelectedEffective =
        hasChildren && childIds.some((id) => isRowCheckedEffective(id));

      if (checked) {
        if (hasChildren && !allChildrenSelectedEffective && !isShiftPressed) {
          lastSelectedIndexRef.current = rowNumber;
          setCurrentParentRowId(row.id);
          setIsParentChecked(true);
          handleRangeSelection(row);
          setShowParentSelectionModal(true);
        } else {
          const newSelectedRows = new Set(selectedRows);
          if (isShiftPressed && lastSelectedIndexRef.current !== null) {
            handleRangeSelection(row);
          } else {
            lastSelectedIndexRef.current = rowNumber;
            newSelectedRows.add(row.id);
            setSelectedRows(newSelectedRows);
            setGlobalUnselectedIds((prev) => {
              const next = new Set(prev);
              next.delete(row.id);
              return next;
            });
          }
        }
      } else if (!isShiftPressed) {
        lastSelectedIndexRef.current = null;
        if (hasChildren && anyChildSelectedEffective) {
          setCurrentParentRowId(row.id);
          setIsParentChecked(false);
          setShowParentSelectionModal(true);
        } else {
          const newSelectedRows = new Set(selectedRows);
          newSelectedRows.delete(row.id);
          setSelectedRows(newSelectedRows);
          if (globalSelection.mode === 'select') {
            setGlobalUnselectedIds((prev) => {
              const next = new Set(prev);
              next.add(row.id);
              return next;
            });
          }
        }
      }
    };

    return (
      <ArrCheckbox
        checked={isChecked}
        size="medium"
        onChange={(checked: boolean) => toggleRow(checked)}
        aria-label={`select-row-${rowNumber}`}
      />
    );
  };

  const transformColumns = (
    columns: Array<any>,
    selectedColumns: IDynamicColDef[]
  ): GridColDef[] => {
    const excludeCloumns = ['ExpandIcon', 'IsSelected', 'Action', 'image'];

    return [
      {
        field: GRID_DETAIL_PANEL_TOGGLE_FIELD,
        headerName: '',
        sortable: false,
        filterable: false,
        disableReorder: true,
        width: 40,
        headerClassName: clsx('typo-text label2_size no-column-separator'),
        renderCell: (params: any) => {
          const rowId = params.row.id;
          const hasChildren =
            Array.isArray(params.row.childDocuments) &&
            params.row.childDocuments.length > 0;

          if (!hasChildren) return null;

          const toggleExpand = (event: React.MouseEvent, rowId: string) => {
            event.stopPropagation();
            setExpandedRowIds((prev) => {
              const newSet = new Set(prev);
              newSet.has(rowId) ? newSet.delete(rowId) : newSet.add(rowId);
              return newSet;
            });
          };

          return (
            <Box
              onClick={(event) => toggleExpand(event, rowId)}
              sx={{
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                overflow: 'hidden',
                whiteSpace: 'nowrap',
                textOverflow: 'ellipsis',
              }}
              data-testid={`grid-detail-toggle-${params.id}`}
            >
              {expandedRowIds.has(rowId) ? (
                <ArrowDropUpIcon />
              ) : (
                <ArrowDropDownIcon />
              )}
            </Box>
          );
        },
      },

      {
        field: 'checkbox',
        sortable: false,
        filterable: false,
        disableReorder: true,
        display: 'flex',
        resizable: true,
        maxWidth: 60,
        headerClassName: clsx('typo-text label2_size'),
        renderHeader: renderAllSelectionCheckbox,
        renderCell: renderSingleSelectionCheckbox,
      },

      ...selectedColumns
        .filter(
          (col: IDynamicColDef) => !excludeCloumns.includes(col.FieldLabel)
        )
        .map((column: IDynamicColDef, index: number) => {
          const fieldName = column.FieldLabel;
          const fieldType = column.FieldDataTye?.toUpperCase().trim();
          if (fieldName === 'DocID') {
            return {
              field: column.FieldIDApi,
              headerName: fieldName,
              flex: 1,
              headerClassName: clsx('typo-text label2_size'),
              cellClassName: clsx('typo-text body3_size', column),

              renderCell: (params: any) => {
                return (
                  <ArrButton
                    onClick={() =>
                      setSelectedDocForPreview({
                        id: params.row.DocumentID,
                        docId: params.row.DocID,
                        docName: params.row.DocFileName,
                      })
                    }
                    variant="text"
                    size="xs"
                    className="id-cell-preview textEllipsis"
                    dataTestId={`${params.row.DocumentID}-preview`}
                  >
                    <CellWrapper
                      cellParams={params}
                      dataTestId={`tooltip-doc-list-docId-${params.id}`}
                      toolTipContent={params.value}
                    >
                      <Typography
                        className="doc-id-cell typo-text body3_size"
                        data-testid={`doc-list-docId-${params.id}`}
                      >
                        {params.value}
                      </Typography>
                    </CellWrapper>
                  </ArrButton>
                );
              },

              renderHeaderFilter: (params: GridRenderHeaderFilterProps) => (
                <FilterTextField
                  dataTestId="docId"
                  gridRenderHeaderFilterProps={params}
                  placeholder={t('common.filter')}
                  className="custom-filter"
                />
              ),
            };
          }
          if (fieldName === 'AIGeneratedSummary') {
            return {
              field: column.FieldIDApi,
              headerName: fieldName,
              flex: 1,
              headerClassName: clsx('typo-text label2_size'),
              cellClassName: clsx('typo-text body3_size', column),

              renderCell: (params: any) => {
                const rowIndex = params.api.getRowIndexRelativeToVisibleRows(
                  params.id
                );
                return (
                  <ArrButton
                    onClick={(e: React.MouseEvent<HTMLElement>) =>
                      showSummary({
                        docId: params.row.DocID,
                        summary: params.value,
                        anchorEl: e.currentTarget,
                      })
                    }
                    variant="text"
                    size="xs"
                    className="id-cell-preview textEllipsis"
                    dataTestId={`${params.row.DocumentID}-summary`}
                  >
                    <CellWrapper
                      cellParams={params}
                      dataTestId={`tooltip-doc-list-text-${column.SequenceNumber}-${params.id}`}
                    >
                      <Typography
                        className="cell-content typo-text body3_size textEllipsis"
                        data-testid={`cell-${fieldName?.replaceAll(' ', '').toLowerCase()}-row-${rowIndex + 1}`}
                      >
                        {params.value}
                      </Typography>
                    </CellWrapper>
                  </ArrButton>
                );
              },

              renderHeaderFilter: (params: GridRenderHeaderFilterProps) => (
                <FilterTextField
                  dataTestId="docId"
                  gridRenderHeaderFilterProps={params}
                  placeholder={t('common.filter')}
                  className="custom-filter"
                />
              ),
            };
          }
          return {
            field: column.FieldIDApi,
            headerName: fieldName,
            flex: 1,
            headerClassName: clsx('typo-text label2_size'),
            cellClassName: clsx('typo-text body3_size'),

            renderCell: (params: any) => {
              if (fieldType === 'DATETIME' || fieldType === 'DATE') {
                const formattedDate = params.value
                  ? convertToUTCDate(params.value, fieldType === 'DATETIME')
                  : '';

                return (
                  <CellWrapper
                    cellParams={params}
                    dataTestId={`tooltip-doc-list-date-${params.id}`}
                    toolTipContent={formattedDate}
                  >
                    <Typography
                      className="cell-content typo-text body3_size textEllipsis"
                      data-testid={`doc-list-date-${params.id}`}
                    >
                      {formattedDate}
                    </Typography>
                  </CellWrapper>
                );
              }

              const rowIndex = params.api.getRowIndexRelativeToVisibleRows(
                params.id
              );

              return (
                <CellWrapper
                  cellParams={params}
                  dataTestId={`tooltip-doc-list-text-${column.SequenceNumber}-${params.id}`}
                  toolTipContent={params.value}
                >
                  <Typography
                    className="cell-content typo-text body3_size textEllipsis"
                    data-testid={`cell-${fieldName?.replaceAll(' ', '').toLowerCase()}-row-${rowIndex + 1}`}
                  >
                    {params.value}
                  </Typography>
                </CellWrapper>
              );
            },

            renderHeaderFilter: (params: GridRenderHeaderFilterProps) => {
              if (fieldType === 'DATETIME' || fieldType === 'DATE') {
                return (
                  <FilterDateField
                    dataTestId={`filter-field-${index}`}
                    gridRenderHeaderFilterProps={params}
                    placeholder={t('common.datePlaceholder')}
                  />
                );
              }

              return (
                <FilterTextField
                  dataTestId={fieldName}
                  gridRenderHeaderFilterProps={params}
                  placeholder={t('common.filter')}
                  className="custom-filter"
                />
              );
            },
          };
        }),
    ];
  };

  const transformRows = (rowsData: Array<any>) => {
    const decodeObjectStrings = (obj: any): any => {
      if (!obj || typeof obj !== 'object') return obj;

      const decoded: any = Array.isArray(obj) ? [] : {};

      for (const key in obj) {
        const value = obj[key];

        if (typeof value === 'string') {
          const parsed = parseDocListDateString(value ?? '');
          decoded[key] = parsed ? getUTCDate(value) : decodeHtmlEntities(value);
        } else if (typeof value === 'object' && value !== null) {
          decoded[key] = decodeObjectStrings(value);
        } else {
          decoded[key] = value;
        }
      }

      return decoded;
    };

    return rowsData.map((row: any) => {
      const parentId = String(row.DocumentID);
      const decodedRow = decodeObjectStrings(row);

      const children = Array.isArray(row.ChildDocuments?.Document)
        ? row.ChildDocuments.Document
        : row.ChildDocuments?.Document
          ? [row.ChildDocuments.Document]
          : [];

      return {
        ...decodedRow,
        id: `parent-${parentId}`,
        image: row?.image ?? '',
        DocListDocExt: row.DocListDocExt,
        childDocuments: children.map((child: any) => {
          const decodedChild = decodeObjectStrings(child);

          return {
            id: `child-${parentId}-${child.DocumentID}`,
            parentDocumentID: parentId,
            DocumentID: child.DocumentID,
            ...decodedChild,
          };
        }),
      };
    });
  };

  const getDetailPanel = ({row}: {row: any}) => {
    const rawChildren = row.childDocuments;
    const children = Array.isArray(rawChildren)
      ? rawChildren
      : rawChildren
        ? [rawChildren]
        : [];

    const validChildren = children.filter(
      (child) =>
        child &&
        typeof child === 'object' &&
        child.DocumentID &&
        String(child.DocumentID) !== String(row.DocumentID)
    );
    if (validChildren.length === 0) {
      return (
        <Typography
          sx={{p: 2}}
          data-testid={`doc-list-no-child-documents-found`}
        >
          No child documents found.
        </Typography>
      );
    }
    return (
      <ArrDataGridPro
        rows={validChildren.map((child: any) => ({
          ...child,
          id: `child-${row.DocumentID}-${child.DocumentID}`,
        }))}
        columns={docListGridHeaders}
        showPanelToggle={true}
        hideFooter
        density="compact"
        className="child-data-grid-pro"
        disableVirtualization
        currentPage={0}
        detailPanelExpandedRowIds={new Set()}
        getDetailPanelHeight={() => 'auto'}
        onDetailPanelExpandedRowIdsChange={() => {}}
        getDetailPanelContent={getDetailPanel}
        onSelectionStateChange={() => {}}
      />
    );
  };

  const docListGridHeaders = useMemo(() => {
    return transformColumns(defaultDataColumns, selectedColumns);
  }, [
    selectedColumns,
    selectedRows,
    docListGridError,
    rows,
    docListGridData,
    globalSelection,
    childScope,
    globalParentUnselectedIds,
    expandedRowIds,
    isShiftPressed,
  ]);

  useEffect(() => {
    const data = transformRows(docListGridData);
    setRows(data);
  }, [docListGridData, filterModel]);

  const handleBackNavigation = () => {
    if (
      backSourcePath &&
      backSourceLabel ===
        t(
          'ai.investigate.manageIndicesDetail.manageIndicesDataGrid.indexDetails'
        )
    ) {
      navigate(backSourcePath, {state: backSourceNavState});
      return;
    }
    if (backSourcePath && backSourceLabel === 'Doc View') {
      setActiveDocument(null);
      if (docList?.prevDocumentListMasterId) {
        dispatch(storeDocumentListMasterId(docList?.prevDocumentListMasterId));
      }
      navigate(backSourcePath);
      return;
    } else if (
      backSourcePath &&
      backSourceLabel === t('docExplorer.header.searchTerm')
    ) {
      navigate(backSourcePath, {
        replace: true,
        state: {
          reportId: extractReportId(backSourcePath),
        },
      });
      return;
    }

    if (backSourcePath && backSourceLabel === t('sessionSearch.title')) {
      const searchId = docList?.backSourceSearchId;

      navigate(backSourcePath, {
        state: {
          highlightSearchId: searchId,
          fromDocList: true,
        },
      });
      return;
    }

    if (backSourcePath) {
      const isABM = backSourcePath
        .toLowerCase()
        .replace(/\/$/, '')
        .includes('advancedbatchmanagementreport');

      if (isABM) {
        const stored = sessionStorage.getItem('reportID');
        const reportID = stored ? Number(stored) : null;

        if (reportID) {
          const url = new URL(backSourcePath, window.location.origin);

          // Replace or add ReportID
          url.searchParams.set('ReportID', reportID.toString());

          // Navigate using the cleaned path + query
          navigate(url.pathname + url.search);
        } else {
          navigate(backSourcePath);
        }
      } else {
        navigate(backSourcePath);
      }
    } else {
      navigate(-1);
    }
  };

  const backButtonText = backSourceLabel
    ? t('docList.backButtonToSource', {source: backSourceLabel})
    : t('docList.backButton');

  const triggerToast = (
    toastType: ToastType | ((prevState: ToastType) => ToastType),
    toastMessage: string
  ) => {
    setShowToast(true);
    setToastType(toastType);
    setToastMessage(toastMessage);
  };

  const saveToProfile = () => {
    const payload = {
      columnFilterSession: {
        columnHeaders: selectedColumns.map((col) => ({
          fieldID: Number(col.FieldID),
          fieldType: col.FieldType,
          fieldName: col.FieldName,
          fieldLabel: col.FieldLabel,
          sequenceNumber: col.SequenceNumber,
          fieldDataTye: col.FieldDataTye,
        })),
      },
    };
    saveToProfileMutate(payload, {
      onSuccess: () => {
        triggerToast(ToastType.Success, t('docList.saveToProfile.saveSuccess'));
        resetSaveToProfile();
        setShowSaveModal(false);
      },
      onError: () => {
        triggerToast(ToastType.Error, t('docList.saveToProfile.saveError'));
      },
    });
  };

  const visibleRowCount = useMemo(() => {
    let count = rows?.length;
    expandedRowIds.forEach((parentId) => {
      const parentRow = rows?.find((r: any) => r?.id === parentId);
      const childCount = parentRow?.ChildDocuments?.Document?.length ?? 0;
      count += childCount;
    });
    return Math.min(count, 10);
  }, [rows, expandedRowIds]);

  const gridHeight = useMemo(() => {
    return (
      DEFAULT_HEIGHT_ADJUSTMENT +
      HEADER_HEIGHT +
      FILTER_HEIGHT +
      visibleRowCount * ROW_HEIGHT
    );
  }, [visibleRowCount]);

  const extractRealIdsAsString = (ids: Set<string | number>): string[] => {
    return Array.from(ids)
      .map((id) => {
        if (typeof id === 'number') return String(id);
        const parts = id.split('-');
        if (parts[0] === 'child' && parts.length >= 3) {
          return parts[parts.length - 1];
        }

        if (parts[0] === 'parent' && parts.length >= 2) {
          return parts[parts.length - 1];
        }
        return null;
      })
      .filter((id): id is string => !!id)
      .reduce((a, b) => {
        return a.includes(b) ? a : [...a, b];
      }, [] as string[]);
  };

  useEffect(() => {
    const isInverse =
      (appliedSelectionOptions.selectAllIntent === 'select' &&
        appliedSelectionOptions.selectAllPages === 'yes') ||
      (appliedSelectionOptions.selectAllIntent === 'unselect' &&
        appliedSelectionOptions.selectAllPages === 'no');

    const isAllChildrenSelected =
      appliedSelectionOptions.selectAllIntent === 'select' &&
      appliedSelectionOptions.selectAllPages === 'yes' &&
      appliedSelectionOptions.selectAllChildren === 'yes';

    setSelectionState({
      IsInverse: isInverse,
      IsAllChildrenSelected: isAllChildrenSelected,
      SelectedIds: isInverse ? [] : extractRealIdsAsString(selectedRows),
      DocsToExclude: isInverse
        ? extractRealIdsAsString(globalUnselectedIds)
        : [],
    });
  }, [
    appliedSelectionOptions,
    selectedRows,
    globalUnselectedIds,
    globalSelection,
  ]);

  useEffect(() => {
    if (showAllSelectionModal && selectAllIntent === 'unselect') {
      setSelectAllChildren('yes');
    }
  }, [showAllSelectionModal, selectAllIntent]);

  return (
    <>
      <Box className="doc-list-header">
        <Box className="header-title">
          <Typography
            className="typo-text headline3_size"
            data-testid="doclist-title"
          >
            {t('docList.title')}
          </Typography>
          <ArrTooltipWrapper
            title={t('docList.toolTipTitle')}
            trigger={<InfoOutlined fontSize="medium" className="info-icon" />}
          >
            <Box
              dangerouslySetInnerHTML={{
                __html: t('docList.toolTipText'),
              }}
            />
          </ArrTooltipWrapper>
        </Box>
        <Box className="header-back-button">
          <ArrButton
            variant="text"
            key="back to source name"
            size="small"
            onClick={handleBackNavigation}
            dataTestId={`back-button-${backSourceLabel?.replace(' ', '').toLowerCase() ?? 'doclist'}`}
          >
            <ArrowBackIcon className="button-arrow" />
            {backButtonText}
          </ArrButton>
        </Box>
      </Box>
      <Box className="doc-list-filter-container">
        <Box className="doc-list-filter">
          <DocListFilterAccordion
            filters={memoizedMetadataFilters}
            title={t('docExplorer.filterAccordion.title')}
            expand={true}
            expandText={t('docExplorer.filterAccordion.expandText')}
            collapseText={t('docExplorer.filterAccordion.collapseText')}
            tooltipTitle={t('docExplorer.filterAccordion.tooltipTitle')}
            tooltipText={t('docExplorer.filterAccordion.tooltipText')}
            onFilterChange={(activeFilters) => {
              const gridFilterItems = activeFilters.map((filter: any) => {
                let value = '';
                if (filter.values && filter.values.length > 0) {
                  const mappedValues = filter.values.map((v: any) => v.text);
                  value = mappedValues.join('|');
                } else if (filter.startDate) {
                  if (filter.endDate) {
                    value = `${filter.startDate}|${filter.endDate}`;
                  } else {
                    value = filter.startDate;
                  }
                } else if (filter.startSize) {
                  if (filter.endSize) {
                    value = `${filter.startSize}|${filter.endSize}`;
                  } else {
                    value = filter.startSize;
                  }
                }
                return {
                  id: filter.id,
                  field: filter.id, // Use id (projectFieldId) for metadata lookup in buildFiltersArray
                  operator:
                    filter.selectionType === 'exclude' ? 'notEquals' : 'equals',
                  value: value,
                  selectionType: filter.selectionType, // Store selectionType for date and file size filters
                };
              });

              const wasFiltersEmpty = filterModel.items.length === 0;
              const isNowEmpty = gridFilterItems.length === 0;
              if (!wasFiltersEmpty && isNowEmpty) {
                lastApiCallRef.current = '';
              }

              setFilterModel({
                items: [...gridFilterItems], // Create new array reference
              });

              if (!wasFiltersEmpty && isNowEmpty) {
                setTimeout(() => {
                  setPageNumber(1);
                }, 0);
              }
            }}
            filterOptions={
              (Array.isArray(loadMetadataFilters?.data)
                ? loadMetadataFilters.data.map((opt) => ({
                    text: opt.text,
                    value: String(opt.key),
                    disabled: false,
                    group: null,
                  }))
                : []) ?? []
            }
            loadingOptions={!loadMetadataFilters}
            errorOptions={false}
          />
        </Box>
      </Box>
      <Box className="doc-list-grid">
        <Box className="doc-action-headers">
          <ActionMenuWrapper
            actions={actionMenuItems}
            sourceType={SourceTypeCode.DOCLIST}
            bulkActionContextTypeCode={SourceTypeCode.DOCUMENT}
            sourceDetails={{
              SourceTypeCode: 'DOCUMENT',
              IsInverse: selectionState.IsInverse,
              DocumentListMasterID: numericSelectedDocListId,
              IsAllChildrenSelected: selectionState.IsAllChildrenSelected,
              SourceID: numericSelectedDocListId,
              SourceDetails: [...selectionState.SelectedIds].map((i) => ({
                SourceID: Number(i),
              })),
              DocsToExclude: [...selectionState.DocsToExclude].map((i) => ({
                SourceID: Number(i),
              })),
              ColumnFilters: updateColumnsWithFilterValues(
                docListPayload.columns as never
              ),
              Filters: buildFiltersArray(),
            }}
            dataTestId="docList-action-menu"
            sourceTypeContext={SourceTypeContextCode.DOCLISTNOC}
            size="small"
            subMenuDirection="right"
            direction="bottom-left"
            menuClassName="doc-list-action-menu"
            isDisabled={!hasAnySelectionAnywhere}
            onActionPick={(id) => {
              onActionSelect(id);
            }}
          />

          <ActionHeader
            showSaveIcon
            showGridList
            hasSelectedRows={hasAnySelectionAnywhere}
            className="ac-header"
            showMangeColumn
            onGridListToggle={onGridListToggle}
            onSaveClick={() => {
              setShowSaveModal(true);
            }}
          />
        </Box>

        <Box className="data-grid-table">
          {showGridView ? (
            <Box className="data-grid-container">
              <Box className="select-all-container">
                {renderAllSelectionCheckbox()}
                <Typography
                  className="typo-text body3_size"
                  data-testid={`doc-list-select-all`}
                >
                  {t('docList.grid.selectAll')}
                </Typography>
              </Box>

              {docListGridLoading && !docListGrid && <ArrLoadingSpinner />}

              <Box className="data-grid-table-thumbnail">
                {rows.map((metadata: any) => (
                  <ImageCardWithDetails
                    key={metadata.DocID}
                    metadata={metadata}
                    imageUrl={metadata.NativeImageFilePath}
                    renderCheckbox={renderSingleSelectionCheckbox}
                  />
                ))}
              </Box>
            </Box>
          ) : (
            <ArrDataGridPro
              showPanelToggle={true}
              rowHeight={ROW_HEIGHT}
              gridHeight={rows.length > 0 ? gridHeight : undefined}
              rows={rows}
              columns={docListGridHeaders}
              getRowClassName={(params) =>
                expandedRowIds.has(params.row.id) ? 'highlight-parent-row' : ''
              }
              sortingMode="server"
              filterMode="server"
              paginationMode="server"
              headerFilters
              disableColumnFilter
              disableColumnResize={false}
              currentPage={pageNumber}
              rowCount={docListGridResult?.totalRecords ?? 0}
              primaryKey="id"
              showPerPageOptions
              loading={docListGridLoading && !docListGridResult}
              noRowsOverlay={NoRowsOverlay}
              detailPanelExpandedRowIds={expandedRowIds}
              getDetailPanelHeight={({row}) =>
                row?.childDocuments?.length
                  ? row.childDocuments.length * 36
                  : 36
              }
              onDetailPanelExpandedRowIdsChange={(newIds: Set<GridRowId>) => {
                setExpandedRowIds(new Set(newIds));
              }}
              getDetailPanelContent={getDetailPanel}
              onSelectionStateChange={() => {}}
              filterModel={filterSecondaryModel}
              sortModel={sortModel}
              onSortModelChange={handleSortModelChange}
              className="doc-list-data-grid-pro"
              hideFooter={true}
              disableVirtualization
              onFilterModelChange={setFilterSecondaryModel}
            />
          )}

          {rows.length ? (
            <Box className="doc-list-grid-pagination">
              <ArrPagination
                perPageOptions={[10, 25, 50, 100, 500]}
                totalItems={docListGridResult?.totalRecords ?? 0}
                currentPage={pageNumber}
                onPageChange={handlePageNumberChange}
                perPage={itemsPerPage}
                onPerPageChange={handleItemsPerPageChange}
                mode="wide"
                entityType="entries"
                showPerPageOptions
                alwaysShow={true}
                isFetching={docListGridLoading}
              />
            </Box>
          ) : null}
        </Box>
        {!!selectedDocForPreview && (
          <DocPreviewModal
            showModal={!!selectedDocForPreview}
            onClose={() => setSelectedDocForPreview(null)}
            document={selectedDocForPreview}
          ></DocPreviewModal>
        )}
      </Box>
      {(isDocViewActionLoading ||
        isReportMasterDocListLoading ||
        isDocumentListIdForActionLoading) && (
        <ArrLoadingSpinner isOpen={true} overlay={true} />
      )}
      <ArrModal
        className="doc-list-modal"
        showModal={showAllSelectionModal}
        onClose={() => setShowAllSelectionModal(false)}
        dataTestId="doc-list-modal"
        buttons={[
          {
            id: 'cancel-selection',
            label: t('docList.modal.selectUnselect.cancel'),
            variant: 'secondary',
            onClick: () => {
              setShowAllSelectionModal(false);
            },
            dataTestId: 'dlm-cancel-btn',
          },
          {
            id: 'confirm-selection',
            label: t('docList.modal.selectUnselect.apply'),
            variant: 'primary',
            dataTestId: 'dlm-apply-btn',
            onClick: () => {
              setAppliedSelectionOptions({
                selectAllIntent,
                selectAllPages: selectAllPages as 'yes' | 'no',
                selectAllChildren: selectAllChildren as 'yes' | 'no',
              });
              setShowAllSelectionModal(false);
              const affectAllPages = selectAllPages === 'yes';
              const includeChildren = selectAllChildren === 'yes';
              if (selectAllIntent === 'select') {
                if (affectAllPages) {
                  const newSelectedRows = new Set<string | number>(
                    selectedRows
                  );
                  if (!includeChildren) {
                    getCurrentPageChildIds().forEach((id) =>
                      newSelectedRows.delete(id)
                    );
                  }
                  setSelectedRows(newSelectedRows);

                  setGlobalUnselectedIds((prev) => {
                    const next = new Set(prev);
                    getCurrentPageParentIds().forEach((id) => next.add(id));
                    if (includeChildren)
                      getCurrentPageChildIds().forEach((id) => next.add(id));
                    return next;
                  });
                  setChildScope((prev) => {
                    prevChildScopeRef.current = {
                      ...prev,
                      includeChildren: globalSelection.includeChildren,
                    };
                    return {mode: 'select', scope: 'allPages'};
                  });
                  setGlobalSelection({mode: 'select', includeChildren});
                  setSelectAllApplied(true);
                  setGlobalUnselectedIds(new Set());
                } else {
                  const newSelectedRows = new Set<string | number>(
                    selectedRows
                  );
                  getCurrentPageParentIds().forEach((id) =>
                    newSelectedRows.add(id)
                  );
                  if (includeChildren) {
                    getCurrentPageChildIds().forEach((id) =>
                      newSelectedRows.add(id)
                    );
                  }
                  setSelectedRows(newSelectedRows);
                  setChildScope((prev) => {
                    prevChildScopeRef.current = {
                      ...prev,
                      includeChildren: globalSelection.includeChildren,
                    };
                    return {mode: 'select', scope: 'currentPage'};
                  });
                  setGlobalSelection({mode: 'none', includeChildren: false});
                  setSelectAllApplied(true);
                }
              } else {
                if (affectAllPages) {
                  const wasGlobalSelectWithChildren =
                    globalSelection.mode === 'select' &&
                    globalSelection.includeChildren;

                  const newSelectedRows = new Set<string | number>(
                    selectedRows
                  );
                  if (!includeChildren && wasGlobalSelectWithChildren) {
                    getCurrentPageChildIds().forEach((id) =>
                      newSelectedRows.add(id)
                    );
                  }

                  getCurrentPageParentIds().forEach((id) =>
                    newSelectedRows.delete(id)
                  );

                  if (includeChildren) {
                    getCurrentPageChildIds().forEach((id) =>
                      newSelectedRows.delete(id)
                    );
                  }

                  setSelectedRows(newSelectedRows);
                  setChildScope((prev) => {
                    prevChildScopeRef.current = {
                      ...prev,
                      includeChildren: globalSelection.includeChildren,
                    };
                    return {mode: 'unselect', scope: 'allPages'};
                  });
                  setGlobalSelection({mode: 'none', includeChildren}); // includeChildren === false
                  setSelectAllApplied(false);
                } else {
                  const newSelectedRows = new Set<string | number>(
                    selectedRows
                  );
                  getCurrentPageParentIds().forEach((id) =>
                    newSelectedRows.delete(id)
                  );
                  if (includeChildren) {
                    getCurrentPageChildIds().forEach((id) =>
                      newSelectedRows.delete(id)
                    );
                  }
                  setSelectedRows(newSelectedRows);

                  setGlobalUnselectedIds((prev) => {
                    const next = new Set(prev);
                    getCurrentPageParentIds().forEach((id) => next.add(id));
                    if (includeChildren)
                      getCurrentPageChildIds().forEach((id) => next.add(id));
                    return next;
                  });

                  setChildScope((prev) => {
                    prevChildScopeRef.current = {
                      ...prev,
                      includeChildren: globalSelection.includeChildren,
                    };
                    return {mode: 'unselect', scope: 'currentPage'};
                  });
                  setGlobalSelection({mode: 'none', includeChildren});
                  setSelectAllApplied(false);
                }
              }
            },
          },
        ]}
      >
        <Box className="doc-list-modal-content">
          <Typography
            className="typo-text headline3_size"
            data-testid={`doc-list-modal-select-unselect-all`}
          >
            {t(
              `docList.modal.selectUnselect.${selectAllIntent === 'select' ? 'select' : 'unselect'}`
            )}
          </Typography>
          <Typography
            className="typo-text body2_size question-text"
            data-testid={`doc-list-select-unselect-question`}
          >
            {t(
              `docList.modal.selectUnselect.${selectAllIntent === 'select' ? 'selectQuestion' : 'unselectQuestion'}`
            )}
          </Typography>
          <ArrRadio
            size="small"
            name="selectAllPages"
            dataTestId="selectAllPages"
            value={selectAllPages}
            onChange={setSelectAllPages}
            data={[
              {label: t('docList.modal.selectUnselect.yes'), value: 'yes'},
              {label: t('docList.modal.selectUnselect.no'), value: 'no'},
            ]}
            className="doc-list-modal-radio"
          />

          <Typography
            className="typo-text body2_size question-text"
            data-testid={`doc-list-typography-link`}
          >
            {t(
              `docList.modal.selectUnselect.${selectAllIntent === 'select' ? 'selectChildrenQuestion' : 'unselectChildrenQuestion'}`
            )}
          </Typography>
          <ArrRadio
            size="small"
            dataTestId="selectAllChildren"
            name="selectAllChildren"
            value={selectAllChildren}
            onChange={setSelectAllChildren}
            data={[
              {label: t('docList.modal.selectUnselect.yes'), value: 'yes'},
              {label: t('docList.modal.selectUnselect.no'), value: 'no'},
            ]}
            className="doc-list-modal-radio"
          />
        </Box>
      </ArrModal>
      <ArrModal
        className="doc-list-modal"
        title={t(
          `docList.modal.parentSelection.${isParentChecked ? 'select' : 'unselect'}`
        )}
        description={t(
          `docList.modal.parentSelection.${isParentChecked ? 'selectChildren' : 'unselectChildren'}`
        )}
        showModal={showParentSelectionModal}
        onClose={() => {
          setShowParentSelectionModal(false);
          setCurrentParentRowId(null);
          setSelectedRangeParentList([]);
        }}
        dataTestId="doc-list-modal-1"
        buttons={[
          {
            id: 'cancel-parent-selection',
            label: t('docList.modal.parentSelection.no'),
            variant: 'secondary',
            dataTestId: 'dlm1-cancel-btn',
            onClick: () => {
              if (currentParentRowId !== null) {
                const newSelectedRows = new Set(selectedRows);
                if (isParentChecked) {
                  // Select only the parent
                  newSelectedRows.add(currentParentRowId);
                  setSelectedRows(newSelectedRows);
                  // Clear parent override so it renders checked
                  setGlobalUnselectedIds((prev) => {
                    const next = new Set(prev);
                    next.delete(currentParentRowId);
                    return next;
                  });
                } else {
                  // Unselect only the parent
                  newSelectedRows.delete(currentParentRowId);
                  setSelectedRows(newSelectedRows);
                  // Under global select, add override so parent renders unchecked
                  if (globalSelection.mode === 'select') {
                    setGlobalUnselectedIds((prev) => {
                      const next = new Set(prev);
                      next.add(currentParentRowId);
                      return next;
                    });
                  }
                }
              }
              setShowParentSelectionModal(false);
              setCurrentParentRowId(null);
              setSelectedRangeParentList([]);
            },
          },
          {
            id: 'confirm-parent-selection',
            label: t('docList.modal.parentSelection.yes'),
            variant: 'primary',
            dataTestId: 'dlm1-confirm-btn',
            onClick: () => {
              if (currentParentRowId !== null) {
                const parentDocId = getParentDocIdFromRowId(currentParentRowId);
                const childIds = getChildIdsForParent(parentDocId);
                const newSelectedRows = new Set(selectedRows);

                if (isParentChecked) {
                  // Select parent + children

                  newSelectedRows.add(`parent-${parentDocId}`);
                  childIds.forEach((id) => newSelectedRows.add(id));
                  setSelectedRows(newSelectedRows);
                  // Clear overrides so all render checked
                  setGlobalUnselectedIds((prev) => {
                    const next = new Set(prev);
                    next.delete(`parent-${parentDocId}`);
                    childIds.forEach((id) => next.delete(id));
                    return next;
                  });
                } else {
                  // Unselect parent + children
                  newSelectedRows.delete(`parent-${parentDocId}`);
                  childIds.forEach((id) => newSelectedRows.delete(id));
                  setSelectedRows(newSelectedRows);
                  // Under global select, add overrides so they render unchecked
                  if (globalSelection.mode === 'select') {
                    setGlobalUnselectedIds((prev) => {
                      const next = new Set(prev);
                      next.add(`parent-${parentDocId}`);
                      childIds.forEach((id) => next.add(id));
                      return next;
                    });
                  }
                }
              } else if (selectedRangeParentList?.length) {
                const newSelectedRows = new Set(selectedRows);
                selectedRangeParentList.forEach((parentId) => {
                  if (isParentChecked) {
                    const parentDocId = getParentDocIdFromRowId(parentId);
                    const childIds = getChildIdsForParent(parentDocId);
                    newSelectedRows.add(`parent-${parentDocId}`);
                    childIds.forEach((id) => newSelectedRows.add(id));
                  }
                });
                setSelectedRows(newSelectedRows);
              }
              setShowParentSelectionModal(false);
              setCurrentParentRowId(null);
              setSelectedRangeParentList([]);
            },
          },
        ]}
      />
      {showManageColumns && (
        <ArrDrawer
          showHeader={false}
          showButton={false}
          isOpen={showManageColumns}
          size="large"
          side="right"
          onClose={() => dispatch(setShowManageColumns(false))}
        >
          <ManageColumns
            currentColumns={selectedColumns}
            onSave={(item) => {
              if (JSON.stringify(item) === JSON.stringify(selectedColumns)) {
                return;
              }
              setSelectedColumns(item);
            }}
          />
        </ArrDrawer>
      )}
      <ArrModal
        dataTestId="save-to-profile-modal"
        showModal={showSaveModal}
        onClose={() => setShowSaveModal(false)}
        title={t('docList.saveToProfile.title')}
        description={t('docList.saveToProfile.description')}
        buttons={[
          {
            id: 'save-no',
            label: t('common.no'),
            variant: 'secondary',
            onClick: () => {
              setShowSaveModal(false);
            },
            dataTestId: 'stp-no-btn',
          },
          {
            id: 'save-yes',
            label: t('common.yes'),
            variant: 'primary',
            onClick: saveToProfile,
            loading: isSaveToProfilePending,
            dataTestId: 'stp-yes-btn',
          },
        ]}
      />
      {showToast && (
        <ArrToastNotification
          message={toastMessage}
          onClose={() => setShowToast(false)}
          status={[toastType]}
        />
      )}
      <ArrDocMaxLimitWarning
        totalDocumentCount={docListGridResult?.totalRecords ?? 0}
        show={showDocLimitWarning}
        destination={SourceTypeCode.DOCVIEW}
        onContinue={onContinueDocLimit}
        onCancel={onCancelDocLimit}
      />

      {summaryModal.enable && (
        <SummaryViewerPopup
          anchorElement={summaryAnchorEl}
          docId={summaryModal.docId}
          summary={summaryModal.summary}
          onClose={() => {
            setSummaryModal((pv) => ({...pv, enable: false}));
            setSummaryAnchorEl(null);
          }}
        />
      )}
    </>
  );
};

export default DocList;
